import React, { useMemo, useState } from 'react';
import {
  Calendar,
  DollarSign,
  FileText,
  User,
  Phone,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  Modal,
  Select,
  Spinner,
  cn,
} from '../components/ui';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useReportsData } from '../hooks/useReportsData';
import { formatSlotLabel } from '../hooks/reportsDataMappers';

const formatInr = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const Reports = () => {
  const currentYear = new Date().getFullYear();
  const [activeTab, setActiveTab] = useState('monthly-slots');
  const [reportYear, setReportYear] = useState(currentYear);
  const [slotPaymentsMonth, setSlotPaymentsMonth] = useState('');
  const [transactionFrom, setTransactionFrom] = useState('');
  const [transactionTo, setTransactionTo] = useState('');
  const [transactionPage, setTransactionPage] = useState(1);
  const [selectedMonthDetails, setSelectedMonthDetails] = useState(null);
  const [monthDetailsLoading, setMonthDetailsLoading] = useState(false);
  const [monthDetailsError, setMonthDetailsError] = useState('');

  const {
    monthlyBookedSlots,
    slotWisePayments,
    slotWiseTransactions,
    transactionPagination,
    loadingMonthly,
    loadingSlotPayments,
    loadingTransactions,
    error,
    loadMonthDetails,
  } = useReportsData({
    year: reportYear,
    slotPaymentsMonth,
    transactionFrom,
    transactionTo,
    transactionPage,
  });

  const monthlyTotals = useMemo(
    () => monthlyBookedSlots.reduce((acc, month) => {
      acc.totalSlots += month.totalSlots;
      return acc;
    }, { totalSlots: 0 }),
    [monthlyBookedSlots]
  );

  const slotPaymentTotals = useMemo(
    () => slotWisePayments.reduce((acc, slot) => {
      acc.totalAmount += slot.totalAmount;
      acc.paidAmount += slot.paidAmount;
      acc.pendingAmount += slot.pendingAmount;
      acc.bookingCount += slot.bookingCount;
      return acc;
    }, { totalAmount: 0, paidAmount: 0, pendingAmount: 0, bookingCount: 0 }),
    [slotWisePayments]
  );

  const transactionTotals = useMemo(
    () => slotWiseTransactions.reduce((acc, transaction) => {
      acc.totalAmount += transaction.amount;
      acc.transactionCount += 1;
      return acc;
    }, { totalAmount: 0, transactionCount: 0 }),
    [slotWiseTransactions]
  );

  const formatPaymentMode = (mode) => {
    if (!mode) return 'N/A';
    const modeMap = {
      cash: 'Cash',
      card: 'Card',
      upi: 'UPI',
      bank_transfer: 'Bank Transfer',
      cheque: 'Cheque',
      online: 'Online',
      other: 'Other',
    };
    return modeMap[mode] || mode.charAt(0).toUpperCase() + mode.slice(1);
  };

  const handleViewMonthDetails = async (month) => {
    setMonthDetailsLoading(true);
    setMonthDetailsError('');
    try {
      const { bookings, pagination } = await loadMonthDetails(month.monthKey);
      setSelectedMonthDetails({
        ...month,
        bookings,
        totalSlots: pagination.total || month.totalSlots,
      });
    } catch (err) {
      // Open the modal with an empty shell so monthDetailsError can render (#112).
      setSelectedMonthDetails({
        ...month,
        bookings: [],
        totalSlots: month.totalSlots,
      });
      setMonthDetailsError(err.message || 'Failed to load month details');
    } finally {
      setMonthDetailsLoading(false);
    }
  };

  const yearOptions = Array.from({ length: 5 }, (_, index) => currentYear - index);

  return (
    <DashboardLayout>
      <div>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-ga-text-primary mb-2">Reports</h1>
          <p className="text-ga-text-muted">View detailed reports on bookings, payments, and transactions</p>
        </div>

        {error && (
          <div className="mb-4 rounded-ga-md border border-ga-danger/30 bg-ga-danger/10 px-4 py-3 text-sm text-ga-danger">
            {error}
          </div>
        )}

        <nav className="mb-6 flex flex-wrap gap-2">
          {[
            { value: 'monthly-slots', label: 'Monthly Booked Slots', Icon: Calendar },
            { value: 'slot-payments', label: 'Slot Wise Payments', Icon: DollarSign },
            { value: 'slot-transactions', label: 'Slot Wise Transactions', Icon: FileText },
          ].map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setActiveTab(value)}
              className={cn(
                'inline-flex items-center gap-2 rounded-ga-full border px-4 py-2 text-sm font-semibold transition-colors',
                activeTab === value
                  ? 'border-ga-primary bg-ga-primary text-ga-text-on-primary'
                  : 'border-ga-border-subtle bg-ga-bg-surface text-ga-text-muted hover:bg-ga-bg-surface-subtle hover:text-ga-text-primary'
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>

        {activeTab === 'monthly-slots' && (
          <div className="space-y-6">
            <Card>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h2 className="text-xl font-semibold text-ga-text-primary">Monthly Booked Slots</h2>
                <div className="flex items-center gap-4 text-sm">
                  <FormField label="Year" className="flex-row items-center gap-2">
                    <Select
                      value={reportYear}
                      onChange={(e) => setReportYear(Number(e.target.value))}
                      className="w-auto min-w-[6rem]"
                    >
                      {yearOptions.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </Select>
                  </FormField>
                  <div className="rounded-ga-sm bg-ga-primary-soft px-4 py-2">
                    <span className="text-ga-text-muted">Total Slots: </span>
                    <span className="font-bold text-ga-primary">{monthlyTotals.totalSlots}</span>
                  </div>
                </div>
              </div>

              {loadingMonthly ? (
                <Spinner label="Loading monthly report…" />
              ) : monthlyBookedSlots.length === 0 ? (
                <EmptyState
                  card
                  icon={Calendar}
                  title="No Bookings Found"
                  description={`No monthly booking data available for ${reportYear}`}
                />
              ) : (
                <>
                  <div className="mb-6 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyBookedSlots}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--ga-border-subtle)" />
                        <XAxis dataKey="month" stroke="var(--ga-text-subtle)" fontSize={12} />
                        <YAxis stroke="var(--ga-text-subtle)" fontSize={12} allowDecimals={false} />
                        <Tooltip
                          formatter={(value) => [value, 'Booked Slots']}
                          contentStyle={{
                            background: 'var(--ga-bg-surface)',
                            border: '1px solid var(--ga-border-subtle)',
                            borderRadius: 8,
                            color: 'var(--ga-text-primary)',
                          }}
                        />
                        <Bar dataKey="totalSlots" fill="var(--ga-primary)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="ga-table min-w-full">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Total Booked Slots</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyBookedSlots.map((month) => (
                          <tr key={month.monthKey}>
                            <td className="whitespace-nowrap font-medium">{month.month}</td>
                            <td className="whitespace-nowrap">{month.totalSlots}</td>
                            <td className="whitespace-nowrap">
                              <Button
                                variant="ghost"
                                onClick={() => handleViewMonthDetails(month)}
                                disabled={monthDetailsLoading}
                                className="px-3 py-1.5 text-ga-primary font-medium border-0 bg-transparent shadow-none"
                              >
                                View Details
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </Card>
          </div>
        )}

        <Modal
          open={Boolean(selectedMonthDetails)}
          onClose={() => {
            setSelectedMonthDetails(null);
            setMonthDetailsError('');
          }}
          title="Monthly Booked Slots Details"
          description={selectedMonthDetails?.month}
          size="xl"
          footer={(
            <Button
              className="w-full"
              onClick={() => {
                setSelectedMonthDetails(null);
                setMonthDetailsError('');
              }}
            >
              Close
            </Button>
          )}
        >
          {selectedMonthDetails && (
            <>
              {monthDetailsError && (
                <div className="mb-4 rounded-ga-md border border-ga-danger/30 bg-ga-danger/10 px-4 py-3 text-sm text-ga-danger">
                  {monthDetailsError}
                </div>
              )}
              {!monthDetailsError && (
                <>
                  <div className="mb-4 flex items-center justify-between">
                    <div className="text-sm text-ga-text-muted">
                      Total Bookings:{' '}
                      <span className="font-semibold text-ga-text-primary">{selectedMonthDetails.totalSlots}</span>
                    </div>
                  </div>

                  {selectedMonthDetails.bookings.length === 0 ? (
                    <EmptyState icon={Calendar} title="No Bookings Found" />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="ga-table min-w-full">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Slot Name / Time</th>
                            <th>Ground / Service</th>
                            <th>Booked On</th>
                            <th>Team Details</th>
                            <th>Booked By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedMonthDetails.bookings.map((booking) => {
                            const bookingDate = booking.date
                              ? (typeof booking.date === 'string' ? parseISO(String(booking.date).slice(0, 10)) : new Date(booking.date))
                              : null;
                            const bookedOnDate = booking.bookingDate
                              ? (typeof booking.bookingDate === 'string' ? parseISO(booking.bookingDate) : new Date(booking.bookingDate))
                              : null;

                            let teamDetailsText = 'N/A';
                            if (booking.teamDetails) {
                              const teams = [];
                              if (booking.teamDetails.teamA) {
                                teams.push(`Team A: ${booking.teamDetails.teamA.name || 'N/A'} (${booking.teamDetails.teamA.captain || 'N/A'})`);
                              }
                              if (booking.teamDetails.teamB) {
                                teams.push(`Team B: ${booking.teamDetails.teamB.name || 'N/A'} (${booking.teamDetails.teamB.captain || 'N/A'})`);
                              }
                              teamDetailsText = teams.length > 0 ? teams.join(' | ') : 'N/A';
                            } else if (booking.bookingType === 'single') {
                              teamDetailsText = 'Single Team Booking';
                            }

                            return (
                              <tr key={booking.id}>
                                <td className="whitespace-nowrap font-medium">
                                  {bookingDate ? format(bookingDate, 'dd MMM yyyy') : 'N/A'}
                                </td>
                                <td>
                                  <div>{formatSlotLabel(booking)}</div>
                                  {booking.slotId && (
                                    <div className="text-xs text-ga-text-subtle">Slot ID: {booking.slotId}</div>
                                  )}
                                </td>
                                <td>
                                  <div className="font-medium">{booking.groundName || 'N/A'}</div>
                                  <div className="text-xs text-ga-text-subtle">{booking.serviceName || 'N/A'}</div>
                                </td>
                                <td className="whitespace-nowrap">
                                  <div>{bookedOnDate ? format(bookedOnDate, 'dd MMM yyyy') : 'N/A'}</div>
                                  {bookedOnDate && (
                                    <div className="text-xs text-ga-text-subtle">{format(bookedOnDate, 'hh:mm a')}</div>
                                  )}
                                </td>
                                <td>
                                  <div className="max-w-xs">{teamDetailsText}</div>
                                </td>
                                <td>
                                  <div className="flex items-start gap-2">
                                    <User className="h-4 w-4 text-ga-text-subtle mt-0.5" />
                                    <div>
                                      <div className="font-medium">
                                        {booking.bookedBy || booking.contactName || 'N/A'}
                                      </div>
                                      {(booking.bookedByPhone || booking.contactPhone) && (
                                        <div className="text-xs text-ga-text-subtle flex items-center gap-1 mt-1">
                                          <Phone className="h-3 w-3" />
                                          {booking.bookedByPhone || booking.contactPhone}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Modal>

        {activeTab === 'slot-payments' && (
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h2 className="text-xl font-semibold text-ga-text-primary">Slot Wise Payments</h2>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <FormField label="Month" className="flex-row items-center gap-2">
                  <Input
                    type="month"
                    value={slotPaymentsMonth}
                    onChange={(e) => setSlotPaymentsMonth(e.target.value)}
                    className="w-auto"
                  />
                </FormField>
                <div className="rounded-ga-sm bg-ga-success/15 px-4 py-2">
                  <span className="text-ga-text-muted">Total Paid: </span>
                  <span className="font-bold text-ga-success">{formatInr(slotPaymentTotals.paidAmount)}</span>
                </div>
                <div className="rounded-ga-sm bg-ga-warning/15 px-4 py-2">
                  <span className="text-ga-text-muted">Pending: </span>
                  <span className="font-bold text-ga-warning">{formatInr(slotPaymentTotals.pendingAmount)}</span>
                </div>
              </div>
            </div>

            {loadingSlotPayments ? (
              <Spinner label="Loading slot payments…" />
            ) : slotWisePayments.length === 0 ? (
              <EmptyState card icon={DollarSign} title="No Payment Data" />
            ) : (
              <div className="overflow-x-auto">
                <table className="ga-table min-w-full">
                  <thead>
                    <tr>
                      <th>Ground / Service</th>
                      <th>Slot</th>
                      <th>Bookings</th>
                      <th>Total Amount</th>
                      <th>Paid</th>
                      <th>Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slotWisePayments.map((slot) => (
                      <tr key={slot.slotKey}>
                        <td>
                          <div className="font-medium">{slot.groundName}</div>
                          <div className="text-xs text-ga-text-subtle">{slot.serviceName}</div>
                        </td>
                        <td className="whitespace-nowrap">{formatSlotLabel(slot)}</td>
                        <td className="whitespace-nowrap">{slot.bookingCount}</td>
                        <td className="whitespace-nowrap font-semibold">{formatInr(slot.totalAmount)}</td>
                        <td className="whitespace-nowrap">
                          <span className="font-medium text-ga-success">{formatInr(slot.paidAmount)}</span>
                        </td>
                        <td className="whitespace-nowrap">
                          <span className={`font-medium ${slot.pendingAmount > 0 ? 'text-ga-warning' : 'text-ga-success'}`}>
                            {formatInr(slot.pendingAmount)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-ga-border-subtle bg-ga-bg-surface-subtle">
                    <tr>
                      <td colSpan="3" className="px-3.5 py-3 text-sm font-bold text-ga-text-primary text-right">Totals:</td>
                      <td className="whitespace-nowrap px-3.5 py-3 text-sm font-bold text-ga-text-primary">{formatInr(slotPaymentTotals.totalAmount)}</td>
                      <td className="whitespace-nowrap px-3.5 py-3 text-sm font-bold text-ga-success">{formatInr(slotPaymentTotals.paidAmount)}</td>
                      <td className="whitespace-nowrap px-3.5 py-3 text-sm font-bold text-ga-warning">{formatInr(slotPaymentTotals.pendingAmount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        )}

        {activeTab === 'slot-transactions' && (
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h2 className="text-xl font-semibold text-ga-text-primary">Slot Wise All Transactions</h2>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <FormField label="From" className="flex-row items-center gap-2">
                  <Input
                    type="date"
                    value={transactionFrom}
                    onChange={(e) => {
                      setTransactionFrom(e.target.value);
                      setTransactionPage(1);
                    }}
                    className="w-auto"
                  />
                </FormField>
                <FormField label="To" className="flex-row items-center gap-2">
                  <Input
                    type="date"
                    value={transactionTo}
                    onChange={(e) => {
                      setTransactionTo(e.target.value);
                      setTransactionPage(1);
                    }}
                    className="w-auto"
                  />
                </FormField>
                <div className="rounded-ga-sm bg-ga-primary-soft px-4 py-2">
                  <span className="text-ga-text-muted">Total Transactions: </span>
                  <span className="font-bold text-ga-primary">{transactionPagination.total || transactionTotals.transactionCount}</span>
                </div>
                <div className="rounded-ga-sm bg-ga-success/15 px-4 py-2">
                  <span className="text-ga-text-muted">Page Total: </span>
                  <span className="font-bold text-ga-success">{formatInr(transactionTotals.totalAmount)}</span>
                </div>
              </div>
            </div>

            {loadingTransactions ? (
              <Spinner label="Loading transactions…" />
            ) : slotWiseTransactions.length === 0 ? (
              <EmptyState card icon={FileText} title="No Transactions Found" />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="ga-table min-w-full">
                    <thead>
                      <tr>
                        <th>Date & Time</th>
                        <th>Ground / Service</th>
                        <th>Slot</th>
                        <th>Customer</th>
                        <th>Amount</th>
                        <th>Payment Mode</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slotWiseTransactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td className="whitespace-nowrap">
                            <div>{format(new Date(transaction.date), 'dd MMM yyyy')}</div>
                            <div className="text-xs text-ga-text-subtle">{format(new Date(transaction.date), 'hh:mm a')}</div>
                          </td>
                          <td>
                            <div className="font-medium">{transaction.groundName}</div>
                            <div className="text-xs text-ga-text-subtle">{transaction.serviceName}</div>
                          </td>
                          <td className="whitespace-nowrap">{formatSlotLabel(transaction)}</td>
                          <td>
                            <div>{transaction.customerName}</div>
                            <div className="text-xs text-ga-text-subtle">{transaction.customerPhone}</div>
                          </td>
                          <td className="whitespace-nowrap">
                            <span className={`font-semibold ${transaction.isInitialAdvance ? 'text-ga-info' : 'text-ga-success'}`}>
                              {formatInr(transaction.amount)}
                            </span>
                          </td>
                          <td className="whitespace-nowrap">{formatPaymentMode(transaction.paymentMode)}</td>
                          <td className="whitespace-nowrap">
                            <Badge variant={transaction.isInitialAdvance ? 'info' : 'success'}>
                              {transaction.transactionType}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {transactionPagination.total > transactionPagination.limit && (
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <Button
                      variant="secondary"
                      disabled={transactionPage <= 1}
                      onClick={() => setTransactionPage((page) => Math.max(1, page - 1))}
                    >
                      Previous
                    </Button>
                    <span className="text-ga-text-muted">
                      Page {transactionPagination.page} of {Math.ceil(transactionPagination.total / transactionPagination.limit)}
                    </span>
                    <Button
                      variant="secondary"
                      disabled={transactionPage * transactionPagination.limit >= transactionPagination.total}
                      onClick={() => setTransactionPage((page) => page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Reports;
